import {
  metricKeys,
  type GameStateSnapshot,
  type MetricKey,
  type MetricReading,
  type RawReading,
  type ValidatedObservation,
} from '@/domain/types'
import type { GamePreset } from '@/presets/types'

type PreviousReadings = { -readonly [Key in MetricKey]?: RawReading[Key] }

interface PendingExpRebaseline {
  readonly level: number
  readonly absolute: number
  readonly ratio: number
  readonly inferredRequired: number
  readonly capturedAt: number
  readonly confirmations: number
}

export class ReadingValidator {
  private previous: PreviousReadings = {}
  private pendingLevelIncrease: { from: number; to: number; capturedAt: number } | null = null
  private pendingExpRebaseline: PendingExpRebaseline | null = null
  private trustedExpRequirement: { level: number; required: number } | null = null
  private mesosNeedsBaseline = false

  constructor(
    private readonly preset: GamePreset,
    private minimumConfidence = 0.62,
  ) {}

  setMinimumConfidence(value: number): void {
    this.minimumConfidence = value
  }

  validate(reading: RawReading): ValidatedObservation {
    if (this.pendingLevelIncrease && reading.capturedAt - this.pendingLevelIncrease.capturedAt > 15_000) {
      this.pendingLevelIncrease = null
    }
    if (reading.mesos.availability === 'unavailable') this.mesosNeedsBaseline = true
    const previousLevel = this.previous.level?.value
    const previousExpRatio = this.previous.exp?.value?.ratio
    const accepted = Object.fromEntries(metricKeys.map((key) => [key, this.acceptMetric(key, reading)])) as Record<MetricKey, boolean>
    const observation: ValidatedObservation = { ...reading, accepted }
    if (accepted.level && typeof previousLevel === 'number' && reading.level.value === previousLevel + 1) {
      this.pendingLevelIncrease = { from: previousLevel, to: reading.level.value, capturedAt: reading.capturedAt }
    }
    if (accepted.exp && typeof previousExpRatio === 'number' && reading.exp.value?.ratio !== null && reading.exp.value!.ratio! < previousExpRatio - 0.08) {
      this.pendingLevelIncrease = null
    }
    if (accepted.level) this.previous.level = reading.level
    if (accepted.exp) this.previous.exp = reading.exp
    if (accepted.hp) this.previous.hp = reading.hp
    if (accepted.mp) this.previous.mp = reading.mp
    if (accepted.mesos) {
      this.previous.mesos = reading.mesos
      this.mesosNeedsBaseline = false
    }
    if (accepted.mapName) this.previous.mapName = reading.mapName
    return observation
  }

  resetBaseline(state: GameStateSnapshot | null = null): void {
    this.previous = state ? {
      ...(state.level === null ? {} : { level: this.seedReading('level', state.level, state) }),
      ...(state.exp.absolute === null && state.exp.ratio === null ? {} : { exp: this.seedReading('exp', state.exp, state) }),
      ...(state.hp.current === null && state.hp.ratio === null ? {} : { hp: this.seedReading('hp', state.hp, state) }),
      ...(state.mp.current === null && state.mp.ratio === null ? {} : { mp: this.seedReading('mp', state.mp, state) }),
      ...(state.mesos === null ? {} : { mesos: this.seedReading('mesos', state.mesos, state) }),
      ...(state.mapName === null ? {} : { mapName: this.seedReading('mapName', state.mapName, state) }),
    } : {}
    this.pendingLevelIncrease = null
    this.pendingExpRebaseline = null
    this.trustedExpRequirement = null
    this.mesosNeedsBaseline = false
  }

  private seedReading<Key extends MetricKey>(metric: Key, value: NonNullable<RawReading[Key]['value']>, state: GameStateSnapshot): MetricReading<typeof value> {
    return {
      value,
      confidence: state.confidence[metric],
      source: this.preset.capabilities[metric] === 'ocr' ? 'ocr' : 'fused',
      availability: 'available',
      capturedAt: state.observedAt?.[metric] ?? state.capturedAt,
    }
  }

  private acceptMetric(metric: MetricKey, reading: RawReading): boolean {
    if (this.preset.capabilities[metric] === 'unavailable') return false

    if (metric === 'level') {
      const candidate = reading.level
      const previous = this.previous.level
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      const value = candidate.value as number
      if (!Number.isInteger(value) || value < 1 || value > 200) return false
      if (previous?.value === null || previous?.value === undefined || value === previous.value || value === previous.value + 1) return true
      return this.levelAgreesWithExp(value, reading)
    }

    if (metric === 'mesos') {
      const candidate = reading.mesos
      const previous = this.previous.mesos
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      const value = candidate.value as number
      if (!Number.isSafeInteger(value) || value < 0) return false
      if (this.mesosNeedsBaseline) return true
      if (previous?.value === null || previous?.value === undefined) return true
      return true
    }

    if (metric === 'mapName') {
      const candidate = reading.mapName
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      const value = candidate.value.trim()
      return value.length >= 2 && value.length <= 32 && /[\u3400-\u9fffA-Za-z0-9]/.test(value)
    }

    if (metric === 'exp') {
      const candidate = reading.exp
      const previous = this.previous.exp
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      const value = candidate.value
      if (value.ratio !== null && (value.ratio < 0 || value.ratio > 1)) return false
      if (value.absolute !== null && value.absolute < 0) return false
      const level = reading.level.value
      const required = typeof level === 'number' ? this.preset.experienceToNext(level) : null
      if (required && value.absolute !== null && value.ratio !== null) {
        const expected = value.absolute / required
        const trustedRequired = this.trustedExpRequirement?.level === level
          ? this.trustedExpRequirement.required
          : null
        const trustedExpected = trustedRequired ? value.absolute / trustedRequired : null
        if (Math.abs(expected - value.ratio) > 0.018
          && (trustedExpected === null || Math.abs(trustedExpected - value.ratio) > 0.018)) {
          return this.confirmExpRebaseline(reading)
        }
      }
      const previousRatio = previous?.value?.ratio
      const previousAbsolute = previous?.value?.absolute
      const ratioReset = typeof previousRatio === 'number'
        && value.ratio !== null
        && (value.ratio < previousRatio - 0.018 || (value.ratio <= 0.001 && previousRatio > 0.001))
      const absoluteReset = typeof previousAbsolute === 'number'
        && value.absolute !== null
        && (value.absolute === 0 && previousAbsolute > 0)
      if (ratioReset || absoluteReset) {
        const simultaneousLevelUp = typeof reading.level.value === 'number'
          && typeof this.previous.level?.value === 'number'
          && reading.level.value === this.previous.level.value + 1
        const pendingLevelUp = this.pendingLevelIncrease !== null
          && reading.level.value === this.pendingLevelIncrease.to
          && reading.capturedAt - this.pendingLevelIncrease.capturedAt <= 15_000
        const plausibleWrap = typeof previousRatio === 'number'
          && previousRatio >= 0.85
          && value.ratio !== null
          && value.ratio <= 0.2
        if (!plausibleWrap || (!simultaneousLevelUp && !pendingLevelUp)) {
          return this.confirmExpRebaseline(reading)
        }
      }
      this.pendingExpRebaseline = null
      return true
    }

    const candidate = metric === 'hp' ? reading.hp : reading.mp
    if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
    const value = candidate.value
    if (value.ratio !== null && (value.ratio < 0 || value.ratio > 1)) return false
    if (value.current !== null && value.current < 0) return false
    if (value.max !== null && (value.max <= 0 || (value.current !== null && value.current > value.max * 1.05))) return false
    return true
  }

  private levelAgreesWithExp(level: number, reading: RawReading): boolean {
    const candidate = reading.exp
    const value = candidate.value
    if (candidate.availability === 'unavailable'
      || candidate.confidence < Math.max(this.minimumConfidence, 0.75)
      || value?.absolute === null
      || value?.absolute === undefined
      || value.ratio === null
      || value.ratio < 0.05) {
      return false
    }
    const requirement = this.preset.experienceToNext(level)
    return requirement !== null
      && requirement > 0
      && Math.abs(value.absolute / requirement - value.ratio) <= 0.018
  }

  private confirmExpRebaseline(reading: RawReading): boolean {
    const candidate = reading.exp
    const value = candidate.value
    const level = reading.level.value
    if (!value || value.absolute === null || value.ratio === null || value.absolute <= 0 || value.ratio <= 0
      || typeof level !== 'number' || candidate.confidence < Math.max(this.minimumConfidence, 0.75)) {
      this.pendingExpRebaseline = null
      return false
    }

    const previous = this.pendingExpRebaseline
    if (previous && candidate.capturedAt <= previous.capturedAt) return false
    const inferredRequired = Math.round(value.absolute / value.ratio)
    const continuesSequence = previous !== null
      && previous.level === level
      && candidate.capturedAt - previous.capturedAt <= 10_000
      && value.absolute >= previous.absolute
      && value.ratio >= previous.ratio - 0.002
      && value.ratio - previous.ratio <= 0.1
      && Math.abs(inferredRequired - previous.inferredRequired) / previous.inferredRequired <= 0.005
    const confirmations = continuesSequence ? previous.confirmations + 1 : 1
    if (confirmations >= 3) {
      this.pendingExpRebaseline = null
      this.trustedExpRequirement = { level, required: inferredRequired }
      return true
    }

    this.pendingExpRebaseline = {
      level,
      absolute: value.absolute,
      ratio: value.ratio,
      inferredRequired,
      capturedAt: candidate.capturedAt,
      confirmations,
    }
    return false
  }
}
